import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useChannels } from '../lib/channels-context';
import { useCurrentUser } from '../lib/auth';
import { Logo } from './Logo';

const NAV = [
  { key: 'home', to: '/', label: 'ホーム', mark: '⌂' },
  { key: 'mine', to: '/mine', label: '視聴履歴', mark: '↺' },
  { key: 'upload', to: '/upload', label: 'アップロード', mark: '↑' },
  { key: 'admin', to: '/admin', label: '管理者ダッシュボード', mark: '▦' },
];

export function Layout(): JSX.Element {
  const [w, setW] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 1440);
  const { channels } = useChannels();
  const nav = useNavigate();
  const user = useCurrentUser();

  useEffect(() => {
    const on = (): void => setW(window.innerWidth);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);

  const compact = w < 900;
  const wide = !compact;
  const shellCols = compact ? '62px minmax(0, 1fr)' : '232px minmax(0, 1fr)';

  return (
    <div style={{ display: 'grid', gridTemplateColumns: shellCols, minHeight: '100vh', background: 'var(--bg)' }}>
      <aside style={sidebarStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 6px' }}>
          <div style={logoBox}>
            <Logo size={18} />
          </div>
          {wide && (
            <div style={{ whiteSpace: 'nowrap' }}>
              <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 16, letterSpacing: '0.01em' }}>
                KooTube
              </div>
            </div>
          )}
        </div>

        <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {NAV.map((n) => (
            <NavLink
              key={n.key}
              to={n.to}
              end={n.to === '/'}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '9px 10px',
                borderRadius: 9,
                background: isActive ? 'var(--surface-tint)' : 'transparent',
                color: isActive ? 'var(--brand)' : 'var(--ink-mid)',
                fontSize: 13.5,
                fontWeight: 500,
                textDecoration: 'none',
                textAlign: 'left',
                width: '100%',
              })}
            >
              <span style={{ width: 18, textAlign: 'center', fontSize: 13, opacity: 0.85, flex: 'none' }}>{n.mark}</span>
              {wide && <span style={{ whiteSpace: 'nowrap' }}>{n.label}</span>}
            </NavLink>
          ))}
        </nav>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {wide && (
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: '0.08em',
                color: 'var(--ink-quiet)',
                padding: '0 10px',
                whiteSpace: 'nowrap',
              }}
            >
              チャンネル
            </div>
          )}
          {channels.map((ch) => (
            <button
              key={ch.channelId}
              onClick={() => nav(`/channels/${ch.channelId}`)}
              style={channelBtn}
            >
              <span style={{ width: 8, height: 8, borderRadius: 3, background: ch.tint, flex: 'none', margin: '0 5px' }} />
              {wide && (
                <>
                  <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {ch.name}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--ink-quiet)' }}>{ch.count}</span>
                </>
              )}
            </button>
          ))}
        </div>

        <div style={{ marginTop: 'auto', borderTop: '1px solid var(--border-soft)', paddingTop: 14, display: 'flex', alignItems: 'center', gap: 9 }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: 'var(--ink-mid)',
              color: '#fff',
              display: 'grid',
              placeItems: 'center',
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {user.initial}
          </div>
          {wide && (
            <div style={{ lineHeight: 1.3, whiteSpace: 'nowrap' }}>
              <div style={{ fontSize: 12.5, fontWeight: 500 }}>{user.name}</div>
            </div>
          )}
        </div>
      </aside>

      <main style={{ minWidth: 0 }}>
        <header style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto' }}>
            <button className="btn" onClick={() => nav('/upload')}>＋ 動画をアップロード</button>
          </div>
        </header>

        <Outlet />
      </main>
    </div>
  );
}

const sidebarStyle: React.CSSProperties = {
  borderRight: '1px solid var(--border)',
  background: 'var(--surface)',
  padding: '22px 11px',
  overflowX: 'hidden',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 26,
  position: 'sticky',
  top: 0,
  height: '100vh',
  boxSizing: 'border-box',
};

const logoBox: React.CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: 9,
  background: '#fff',
  border: '1px solid var(--border)',
  display: 'grid',
  placeItems: 'center',
  fontSize: 18,
  lineHeight: 1,
};

const channelBtn: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  padding: '7px 10px',
  border: 'none',
  borderRadius: 9,
  background: 'transparent',
  fontSize: 13,
  color: 'var(--ink-mid)',
  cursor: 'pointer',
  textAlign: 'left',
  width: '100%',
};

const headerStyle: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 5,
  background: 'rgba(245,247,251,0.92)',
  backdropFilter: 'blur(10px)',
  borderBottom: '1px solid var(--border)',
  padding: '14px 30px',
  display: 'flex',
  alignItems: 'center',
  gap: 16,
};
